'use client';

import React from 'react'; // Ensure React is imported for React.ReactNode

// GameControllerから渡されるPropsの型定義
interface TypingAreaProps {
  currentLineText: string; // 表示する現在の行のテキスト
  userInput: string; // ユーザーの現在の入力
  onInputChange: (value: string) => void; // 入力変更時のコールバック
  isReadOnly?: boolean; // 入力フィールドを読み取り専用にするか
  // TODO: タイプミス時の視覚的フィードバックのための情報 (例: ミスした文字の位置など)
  // TODO: 正しい指使いガイド表示のための情報
}

const TypingArea: React.FC<TypingAreaProps> = ({
  currentLineText,
  userInput,
  onInputChange,
  isReadOnly = false,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    // 入力値から英数字と記号（ASCII表示可能文字の範囲）以外を削除します。
    // \x20 はスペース、\x7E はチルダ(~)です。この範囲に英数字と基本的な記号が含まれます。
    // [^...] は指定範囲外の文字にマッチし、g フラグで文字列全体から該当文字を全て置換（削除）します。
    const filteredValue = value.replace(/[^\x20-\x7E]/g, '');
    onInputChange(filteredValue);
  };

  // お手本コードの表示部分のスタイルを調整するヘルパー関数
  const getStyledLineText = () => {
    if (!currentLineText) return null;

    // 行頭スペースをトリムした比較用のテキストを作成
    const trimmedCurrentLineText = currentLineText.trimStart();
    const trimmedUserInput = userInput.trimStart();

    const parts: React.ReactNode[] = [];
    // お手本テキストの先頭のスペースを保持
    const leadingSpaces = currentLineText.match(/^(\s*)/)?.[0] || '';
    if (leadingSpaces) {
      parts.push(<span key="leading-space">{leadingSpaces}</span>);
    }

    let correctPart = '';
    let incorrectPart = '';
    let remainingPartProcessed = false;

    for (let i = 0; i < trimmedCurrentLineText.length; i++) {
      if (i < trimmedUserInput.length) {
        if (trimmedUserInput[i] === trimmedCurrentLineText[i]) {
          if (incorrectPart) {
            parts.push(
              <span key={`incorrect-${parts.length}`} className="text-red-500">
                {incorrectPart}
              </span>,
            );
            incorrectPart = '';
          }
          correctPart += trimmedCurrentLineText[i];
        } else {
          if (correctPart) {
            parts.push(
              <span key={`correct-${parts.length}`} className="text-green-500">
                {correctPart}
              </span>,
            );
            correctPart = '';
          }
          incorrectPart += trimmedCurrentLineText[i];
        }
      } else {
        if (correctPart) {
          parts.push(
            <span key={`correct-${parts.length}`} className="text-green-500">
              {correctPart}
            </span>,
          );
          correctPart = '';
        }
        if (incorrectPart) {
          parts.push(
            <span key={`incorrect-${parts.length}`} className="text-red-500">
              {incorrectPart}
            </span>,
          );
          incorrectPart = '';
        }
        const remainingText = trimmedCurrentLineText.substring(i);
        parts.push(
          <span key={`remaining-${parts.length}`} className="text-gray-400">
            {remainingText}
          </span>,
        );
        remainingPartProcessed = true;
        break;
      }
    }

    if (correctPart) {
      parts.push(
        <span key={`correct-${parts.length}`} className="text-green-500">
          {correctPart}
        </span>,
      );
    }
    if (incorrectPart) {
      parts.push(
        <span key={`incorrect-${parts.length}`} className="text-red-500">
          {incorrectPart}
        </span>,
      );
    }

    // ユーザー入力がトリムされたお手本テキストより長く、まだ残りの部分が処理されていない場合
    if (!remainingPartProcessed && trimmedUserInput.length > trimmedCurrentLineText.length) {
      const extraInput = trimmedUserInput.substring(trimmedCurrentLineText.length);
      parts.push(
        <span key={`extra-incorrect-${parts.length}`} className="text-red-500">
          {extraInput}
        </span>,
      );
    }

    return parts;
  };

  return (
    <div className="w-full max-w-2xl p-4 border rounded-lg shadow">
      <div
        className="mb-4 p-3 bg-gray-100 rounded font-mono text-lg break-all"
        style={{ minHeight: '3em' }}
      >
        {currentLineText ? (
          getStyledLineText()
        ) : (
          <span className="text-gray-400">コードを読み込み中...</span>
        )}
      </div>
      <input
        type="text"
        value={userInput}
        onChange={handleChange}
        readOnly={isReadOnly}
        className="w-full p-3 border rounded font-mono text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder={isReadOnly ? 'ゲーム開始待ち...' : 'ここにタイピングしてください'}
        // TODO: ゲームの状態に応じて autoFocus を制御
      />
      {/* TODO: 正しい指使いのガイドを表示するエリア */}
      {/* <div className="mt-4 text-sm text-gray-600">
        <p>正しい指使いガイド（仮）</p>
      </div> */}
    </div>
  );
};

export default TypingArea;
